import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useSyncCockpitPublicationMutation } from "../api";
import { PublicationComparison } from "./PublicationComparison";
import { useCockpitPublicationById } from "../api/useCockpitPublicationById";
import { useCockpitPublicationFetcher } from "../api/useCockpitPublications";
import type { CockpitPublicationItem } from "../api/useCockpitPublications";

import { AsyncSelect } from "@/components/ui/v2/async-select";
import { buttonVariants } from "@/components/ui/v2/button-variants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/v2/popover";
import { ModuleName } from "@/features/integrations/utils/module-name";
import {
  useCockpitWorkflowStatuses,
  type CockpitWorkflowStatus,
} from "@/features/recruitnow/workflow-statuses/api/useCockpitWorkflowStatuses";
import { vacancyQueryKeys } from "@/features/vacancies/api/queryKeys";
import { useVacancies } from "@/features/vacancies/api/useVacancies";
import { handleProblemDetailsError } from "@/lib/helpers/handleProblemDetailsError";
import useToast from "@/lib/hooks/useToast";
import { cn } from "@/lib/utils/cn";

export function PublicationsTabContent() {
  const { t } = useTranslation();
  const { clientId } = useParams<{ clientId: string }>();
  const { showToast } = useToast();
  const [selectedPublicationId, setSelectedPublicationId] = useState("");
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);

  const queryClient = useQueryClient();
  const { data: workflowStatuses } = useCockpitWorkflowStatuses(
    clientId,
    ModuleName.JobSource
  );

  const stableStatusIds = useMemo(
    () => selectedStatusIds,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStatusIds.join(",")]
  );
  const paginatedFetcher = useCockpitPublicationFetcher(
    clientId,
    stableStatusIds.length > 0 ? stableStatusIds : undefined
  );
  const syncMutation = useSyncCockpitPublicationMutation();
  const { data: publication, isLoading: isLoadingPublication } = useCockpitPublicationById(
    clientId,
    selectedPublicationId || undefined
  );
  const { data: vacanciesResponse, isLoading: isLoadingVacancy } = useVacancies({
    clientId: clientId ?? undefined,
    publicationIds: selectedPublicationId ? [selectedPublicationId] : [""],
    page: 1,
    pageSize: 1,
  });
  const vacancy = vacanciesResponse?.data?.[0] ?? null;

  function toggleStatus(statusId: string) {
    setSelectedPublicationId("");
    setSelectedStatusIds((prev) =>
      prev.includes(statusId)
        ? prev.filter((id) => id !== statusId)
        : [...prev, statusId]
    );
  }

  function getStatusLabel(statuses: CockpitWorkflowStatus[]) {
    if (selectedStatusIds.length === 0) {
      return t("integrations.cockpit.jobSource.publications.allStatuses", "All statuses");
    }
    if (selectedStatusIds.length === 1) {
      const match = statuses.find((s) => s.id === selectedStatusIds[0]);
      return match?.name ?? selectedStatusIds[0];
    }
    return `${selectedStatusIds.length} statuses`;
  }

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          {t("integrations.cockpit.jobSource.publications.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("integrations.cockpit.jobSource.publications.description")}
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
          {workflowStatuses && workflowStatuses.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">
                {t("integrations.cockpit.jobSource.publications.statusLabel", "Status")}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      buttonVariants({ variant: "outline" }),
                      "w-[180px] justify-between gap-2 text-left"
                    )}
                  >
                    <span className="truncate">{getStatusLabel(workflowStatuses)}</span>
                    <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[180px] p-1">
                  {workflowStatuses.map((status) => {
                    const selected = selectedStatusIds.includes(status.id);
                    return (
                      <button
                        key={status.id}
                        type="button"
                        onClick={() => toggleStatus(status.id)}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                      >
                        <Check
                          className={cn(
                            "size-4 shrink-0",
                            selected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="truncate">{status.name ?? status.id}</span>
                      </button>
                    );
                  })}
                </PopoverContent>
              </Popover>
            </div>
          )}
          <AsyncSelect<CockpitPublicationItem>
            paginatedFetcher={paginatedFetcher}
            getOptionValue={(opt) => opt.id}
            getDisplayValue={(opt) => opt.name || opt.id}
            renderOption={(opt) => (
              <span className="truncate">{opt.name || opt.id}</span>
            )}
            value={selectedPublicationId}
            onChange={setSelectedPublicationId}
            label={t("integrations.cockpit.jobSource.publications.selectLabel")}
            placeholder={t("integrations.cockpit.jobSource.publications.selectPlaceholder")}
            disabled={syncMutation.isPending || !clientId}
            width={280}
            noResultsMessage={t("integrations.cockpit.jobSource.publications.noResults")}
            clearable
          />
        </div>
      </section>

      {selectedPublicationId && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t("integrations.cockpit.jobSource.publications.comparison.title")}
          </h2>
          <PublicationComparison
            publication={publication ?? null}
            vacancy={vacancy ?? null}
            isLoadingPublication={isLoadingPublication}
            isLoadingVacancy={isLoadingVacancy}
            onSync={
              clientId && selectedPublicationId
                ? () =>
                  syncMutation.mutate(
                    { clientId, publicationId: selectedPublicationId },
                    {
                      onSuccess: () => {
                        showToast(
                          t("integrations.cockpit.jobSource.syncPublication.success"),
                          "success"
                        );
                        queryClient.invalidateQueries({
                          queryKey: vacancyQueryKeys.list({
                            clientId,
                            publicationIds: [selectedPublicationId],
                            page: 1,
                            pageSize: 1,
                          }),
                        });
                      },
                      onError: (
                        error: AxiosError<{
                            title?: string;
                            detail?: string;
                            status?: number;
                          }>
                      ) => {
                        const detail = error.response?.data?.detail;
                        if (error.response?.status === 403 && detail) {
                          showToast(detail, "error");
                          return;
                        }
                        handleProblemDetailsError({
                          error: error as AxiosError<{
                              title: string;
                              detail: string;
                              status?: number;
                            }>,
                          t,
                          showToast,
                          fallbackMessage: t(
                            "integrations.cockpit.jobSource.syncPublication.error"
                          ),
                        });
                      },
                    }
                  )
                : undefined
            }
            isSyncing={syncMutation.isPending}
          />
        </section>
      )}
    </div>
  );
}
